import { useState, useEffect } from "react";
import {
  useGetAllBatchTimingQuery,
  useAddBatchTimingMutation,
  useGetBatchTimingByIdQuery,
  useUpdateBatchTimingMutation,
  useDeleteBatchTimingMutation,
} from "../api/batchTimingApi";
import { BatchTimingPayloadType } from "../types/batchTimingRequest";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";
import { showToast } from "../../../../core/utils/alert";
import { PaginationProps } from "../../../../core/data/interface";
import { all_routes } from "../../../router/all_routes";
import { BatchTimingResponseType } from "../types/batchTimingResponse";

const EMPTY_BATCH_TIMINGS: BatchTimingResponseType[] = [];

const convertTo12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "pm" : "am";
  const hour12 = hour % 12 || 12;
  return `${hour12.toString().padStart(2, "0")}:${minutes}${ampm}`;
};

export const useBatchTiming = (params?: PaginationProps & { skip?: boolean }) => {
  const { skip, ...queryParams } = params || {};
  const { data, isLoading, isError, error } = useGetAllBatchTimingQuery(
    queryParams,
    { skip }
  );

  const [
    addBatchTimingMutation,
    { isLoading: isAdding, isError: isAddError, error: addError },
  ] = useAddBatchTimingMutation();

  const [
    updateBatchTimingMutation,
    { isLoading: isUpdating, isError: isUpdateError, error: updateError },
  ] = useUpdateBatchTimingMutation();

  const [
    deleteBatchTimingMutation,
    { isLoading: isDeleting, isError: isDeleteError, error: deleteError },
  ] = useDeleteBatchTimingMutation();

  const paginationData = {
    totalPages: data?.data?.totalPages,
    pageSize: data?.data?.pageSize,
    totalItems: data?.data?.totalItems,
    currentPage: data?.data?.currentPage,
  };

  const errorMessage = isError ? getApiErrorMessage(error) : null;
  const addErrorMessage = isAddError ? getApiErrorMessage(addError) : null;
  const updateErrorMessage = isUpdateError
    ? getApiErrorMessage(updateError)
    : null;
  const deleteErrorMessage = isDeleteError
    ? getApiErrorMessage(deleteError)
    : null;

  const addBatchTiming = async (payload: BatchTimingPayloadType) => {
    try {
      const formattedPayload = {
        ...payload,
        start_time: convertTo12Hour(payload.start_time),
        end_time: convertTo12Hour(payload.end_time),
      };

      const response = await addBatchTimingMutation(formattedPayload).unwrap();
      showToast({
        title: "Success",
        text: "Batch timing added successfully!",
        icon: "success",
        timer: 3000,
      });
      return {
        success: true,
        data: response.data,
        redirectTo: all_routes.batchTimingList,
      };
    } catch (error) {
      showToast({
        title: "Error",
        text: addErrorMessage || "Failed to add batch timing",
        icon: "error",
        timer: 3000,
      });
      return { success: false, error: addErrorMessage };
    }
  };

  const updateBatchTiming = async (
    id: string,
    payload: BatchTimingPayloadType
  ) => {
    try {
      const formattedPayload = {
        ...payload,
        start_time: convertTo12Hour(payload.start_time),
        end_time: convertTo12Hour(payload.end_time),
      };

      const response = await updateBatchTimingMutation({
        id,
        payload: formattedPayload,
      }).unwrap();
      showToast({
        title: "Success",
        text: "Batch timing updated successfully!",
        icon: "success",
        timer: 3000,
      });
      return { success: true, data: response.data };
    } catch (error) {
      showToast({
        title: "Error",
        text: updateErrorMessage || "Failed to update batch timing",
        icon: "error",
        timer: 3000,
      });
      return { success: false, error: updateErrorMessage };
    }
  };

  const deleteBatchTiming = async (id: string) => {
    try {
      await deleteBatchTimingMutation({ id }).unwrap();
      showToast({
        title: "Success",
        text: "Batch timing deleted successfully!",
        icon: "success",
      });
      return { success: true };
    } catch (error) {
      showToast({
        title: "Error",
        text: deleteErrorMessage || "Failed to delete batch timing",
        icon: "error",
      });
      return { success: false, error: deleteErrorMessage };
    }
  };

  return {
    batchTimings: data?.data?.items ?? EMPTY_BATCH_TIMINGS,
    isLoading,
    error: errorMessage,
    paginationData: {
      totalPages: data?.data?.totalPages,
      pageSize: data?.data?.pageSize,
      totalItems: data?.data?.totalItems,
      currentPage: data?.data?.currentPage,
    },
    addBatchTiming,
    updateBatchTiming,
    deleteBatchTiming,
    isAdding,
    isUpdating,
    isDeleting,
    addError: addErrorMessage,
    updateError: updateErrorMessage,
    deleteError: deleteErrorMessage,
  };
};
